import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Download, Calculator, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/AppLayout";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, usePoll } from "@/lib/api";
import { money } from "@/pages/Awards";
import { toast } from "sonner";

const blank = { label: "", amount: "", notes: "" };

function BudgetForm({ onCreated }) {
  const [f, setF] = useState({ title: "", duration_months: 12, cost_share_pct: 0, indirect_rate_pct: 0 });
  const [lines, setLines] = useState([{ label: "Payroll", amount: "", notes: "" }, { label: "Equipment", amount: "", notes: "" }, { label: "Rent", amount: "", notes: "" }]);
  const [busy, setBusy] = useState(false);
  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/budgets", { ...f, lines: lines.filter((l) => l.label && l.amount !== "").map((l) => ({ ...l, amount: Number(l.amount) })) });
      onCreated(data.budget_id);
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };
  const total = lines.reduce((s, l) => s + Number(l.amount || 0), 0);
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="budget-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2"><Label>Project name</Label><Input required className="mt-1.5" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="budget-title-input" /></div>
        <div><Label>Months</Label><Input type="number" min="1" className="mt-1.5" value={f.duration_months} onChange={(e) => setF({ ...f, duration_months: Number(e.target.value) })} data-testid="budget-months-input" /></div>
        <div><Label>Your match %</Label><Input type="number" min="0" max="100" className="mt-1.5" value={f.cost_share_pct} onChange={(e) => setF({ ...f, cost_share_pct: Number(e.target.value) })} data-testid="budget-match-input" /></div>
        <div><Label>Indirect rate %</Label><Input type="number" min="0" max="60" className="mt-1.5" value={f.indirect_rate_pct} onChange={(e) => setF({ ...f, indirect_rate_pct: Number(e.target.value) })} data-testid="budget-indirect-input" /></div>
      </div>
      <div>
        <Label>Expected costs for the whole project</Label>
        <div className="mt-2 space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <Input className="col-span-4" placeholder="Cost (e.g. 2 staff salaries)" value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} data-testid={`budget-line-label-${i}`} />
              <Input className="col-span-3" type="number" min="0" placeholder="Amount $" value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} data-testid={`budget-line-amount-${i}`} />
              <Input className="col-span-4" placeholder="Notes (optional)" value={l.notes} onChange={(e) => setLine(i, "notes", e.target.value)} data-testid={`budget-line-notes-${i}`} />
              <button type="button" onClick={() => setLines(lines.filter((_, j) => j !== i))} className="col-span-1 text-slate-300 hover:text-red-600" data-testid={`budget-line-remove-${i}`}><Trash2 className="h-4 w-4 mx-auto" /></button>
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setLines([...lines, { ...blank }])} data-testid="budget-add-line-button"><Plus className="h-3.5 w-3.5 mr-1" />Add cost</Button>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 pt-4">
        <span className="text-sm text-slate-500">Entered: <b className="text-slate-900">{money(total)}</b></span>
        <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="budget-submit-button">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}Build budget proposal</Button>
      </div>
    </form>
  );
}

function BudgetResult({ b }) {
  const r = b.result || {};
  const pdf = async () => {
    const res = await api.get(`/budgets/${b.budget_id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `${b.title}_budget.pdf`;
    a.click();
  };
  return (
    <div className="space-y-4 fade-up" data-testid="budget-result">
      <div className="bg-slate-900 text-white rounded-lg p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[["Total project", r.total_project_cost], ["Funder request", r.federal_request], ["Your match", r.applicant_match], ["Indirect", r.indirect_total]].map(([l, v]) => (
          <div key={l}><div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{l}</div><div className="font-display text-2xl font-extrabold mt-1">{money(v)}</div></div>
        ))}
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{r.narrative}</p>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {(r.categories || []).map((c) => (
          <div key={c.category} className="p-4">
            <div className="flex justify-between"><span className="font-semibold text-slate-900">{c.category}</span><span className="font-semibold">{money(c.amount)}</span></div>
            <p className="text-xs text-slate-500 mt-1">{c.justification}</p>
          </div>
        ))}
      </div>
      {r.compliance_flags?.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900"><div className="font-semibold flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4" />Compliance flags</div><ul className="list-disc pl-5 space-y-1">{r.compliance_flags.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
      <Button onClick={pdf} variant="outline" data-testid="budget-download-pdf-button"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
    </div>
  );
}

export default function Budget() {
  const { hasPkg } = useAuth();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/budgets").then((r) => { setItems(r.data); return r.data; }), []);
  useEffect(() => { if (hasPkg("proposal_writer")) load(); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load, items.some((b) => b.status === "pending"), 3000);
  const current = items.find((b) => b.budget_id === sel);
  if (!hasPkg("proposal_writer")) return (<div><PageHeader eyebrow="Area 01 · Budget Forecaster" title="AI budget forecaster" /><UpgradeGate pkg="proposal_writer" title="Unlock the AI Budget Forecaster">Enter your expected costs and get a structured grant budget mapped to standard federal/state object-class categories, with justifications and a PDF.</UpgradeGate></div>);
  return (
    <div>
      <PageHeader eyebrow="Area 01 · Budget Forecaster" title="AI budget forecaster">Enter expected costs; GrantVerify maps them to standard SF-424A categories with justifications, indirect costs and match.</PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-6">
          <BudgetForm onCreated={(id) => { setSel(id); load(); }} />
          {current?.status === "pending" && <div className="bg-white border border-dashed border-emerald-300 rounded-lg p-6 flex items-center gap-3" data-testid="budget-pending"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" />Building your budget proposal…</div>}
          {current?.status === "error" && <p className="text-red-600 text-sm">Budget generation failed. Try again.</p>}
          {current?.status === "ready" && <BudgetResult b={current} />}
        </div>
        <div className="lg:col-span-5">
          <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Saved budgets</h3>
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {items.length === 0 && <p className="p-4 text-sm text-slate-500">None yet.</p>}
            {items.map((b) => (
              <button key={b.budget_id} onClick={() => setSel(b.budget_id)} data-testid={`budget-item-${b.budget_id}`} className={`w-full text-left p-4 flex justify-between gap-2 transition-colors ${sel === b.budget_id ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
                <span className="text-sm font-medium text-slate-900">{b.title}</span>
                <span className="text-xs text-slate-500">{b.status === "ready" ? money(b.result?.total_project_cost) : b.status}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
